import React, { ReactNode } from 'react';
import {
    Grid,
    Skeleton,
    styled,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
    Typography,
} from '@mui/material';

import { formatCurrency } from '../../utils/formattingUtils';
import { ValueReferences } from '../../utils/enums';
import DonutGraph from '../Graphs/DonutGraph';

const classesPrefix = 'averageRate';

const classes = {
    graphWrapper: `${classesPrefix}-graphWrapper`,
    avgRateGraphContainer: `${classesPrefix}-avgRateGraphContainer`,
    avgRateText: `${classesPrefix}-avgRateText`,
    table: `${classesPrefix}-table`,
    rightTableCol: `${classesPrefix}-rightTableCol`,
    colorSwatch: `${classesPrefix}-colorSwatch`,
    lineHaulRate: `${classesPrefix}-lineHaulRate`,
    fuelRate: `${classesPrefix}-fuelRate`,
    accessorialCharges: `${classesPrefix}-accessorialCharges`,
    centered: `${classesPrefix}-centered`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`& .${classes.graphWrapper}`]: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
        },
        [`& .${classes.avgRateGraphContainer}`]: {
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: theme.spacing(1),
        },
        [`& .${classes.avgRateText}`]: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        },
        [`& .${classes.table}`]: {
            '& td': {
                borderBottom: 'none',
                paddingLeft: 0,
                paddingRight: 0,
            },
        },
        [`& .${classes.rightTableCol}`]: {
            textAlign: 'right',
            fontWeight: 'bold',
        },
        [`& .${classes.colorSwatch}`]: {
            display: 'inline-block',
            height: theme.spacing(2),
            width: theme.spacing(2),
            marginRight: theme.spacing(1),
        },
        [`& .${classes.lineHaulRate}`]: {
            backgroundColor: theme.palette.dataVisualization[2][0],
        },
        [`& .${classes.fuelRate}`]: {
            backgroundColor: theme.palette.dataVisualization[2][1],
        },
        [`& .${classes.accessorialCharges}`]: {
            backgroundColor: theme.palette.dataVisualization[2][2],
        },
        [`& .${classes.centered}`]: {
            display: 'flex',
            alignItems: 'center',
        },
    };
});

const AverageRate = ({
    loading,
    totalRevenue,
    linehaulRevenue,
    fuelRevenue,
    accessorialRevenue,
    loadedMiles,
}: {
    loading: boolean;
    totalRevenue: number | null;
    linehaulRevenue: number | null;
    fuelRevenue: number | null;
    accessorialRevenue: number | null;
    loadedMiles: number | null;
}): React.JSX.Element => {
    const avgRate = totalRevenue !== null && loadedMiles ? totalRevenue / loadedMiles : null;
    const baseRate = linehaulRevenue !== null && loadedMiles ? linehaulRevenue / loadedMiles : null;
    const fuelRate = fuelRevenue !== null && loadedMiles ? fuelRevenue / loadedMiles : null;
    const accCharges = accessorialRevenue !== null && loadedMiles ? accessorialRevenue / loadedMiles : null;
    const graphData = [
        { value: baseRate ?? 0 },
        { value: fuelRate ?? 0 },
        { value: accCharges ?? 0 },
    ];

    const renderTableCellValue = (inputValue: number | null): ReactNode => {
        if (loading) {
            return <Skeleton />;
        }

        return formatCurrency(inputValue);
    };

    const renderAverageRate = (): ReactNode => {
        if (loading) {
            return <Skeleton />;
        }

        if (avgRate === null) {
            return (
                <Tooltip title='Rate data unavailable'>
                    <span>{ValueReferences.NA}</span>
                </Tooltip>
            );
        }

        return formatCurrency(avgRate);
    };

    return (
        <StyledGrid container direction='column' justifyContent='flex-end'>
            <Grid item xs='auto'>
                <Typography variant='h4'>Total RPM</Typography>
            </Grid>
            <Grid className={classes.graphWrapper} item xs>
                <div className={classes.avgRateGraphContainer}>
                    <DonutGraph
                        loading={loading}
                        data={graphData}
                    />
                    <Typography
                        variant='h6'
                        className={classes.avgRateText}
                    >
                        {renderAverageRate()}
                    </Typography>
                </div>
            </Grid>
            <Grid item xs='auto'>
                <Table className={classes.table} size='small'>
                    <TableBody>
                        <TableRow>
                            <TableCell className={classes.centered}>
                                <div className={`${classes.colorSwatch} ${classes.lineHaulRate}`} />
                                Line Haul Rate
                            </TableCell>
                            <TableCell className={classes.rightTableCol}>
                                {renderTableCellValue(baseRate)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.centered}>
                                <div className={`${classes.colorSwatch} ${classes.fuelRate}`} />
                                Fuel Rate
                            </TableCell>
                            <TableCell className={classes.rightTableCol}>
                                {renderTableCellValue(fuelRate)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.centered}>
                                <div className={`${classes.colorSwatch} ${classes.accessorialCharges}`} />
                                Accessorial Charges
                            </TableCell>
                            <TableCell className={classes.rightTableCol}>
                                {renderTableCellValue(accCharges)}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Grid>
        </StyledGrid>
    );
};

export default AverageRate;
